import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type {
  PayoutDestinationAwaiting,
  PayoutDestinationPendingChange,
  PayoutDestinationState,
  PayoutDestinationVersion,
  PayoutDestinationView,
  RequestPayoutDestinationResponse,
} from "../../api/types";
import { useSession } from "../../auth/AuthProvider";
import { CommandError } from "../../components/CommandError";
import { RejectionDetail } from "../../components/RejectionDetail";
import { Banner, Button, ButtonLink, Card, Dialog, PageHeader, QueryView, StatusPill, Table, type Tone } from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { externalNav } from "../../lib/externalNav";
import { canChangePayoutDestination } from "../../lib/roles";
import { useIntentKey } from "../../lib/useIntentKey";
import { SINGLE_USER_FALLBACK_TEXT } from "../shared/org/orgCopy";

// The payout destination (G-42, migration 0065; UX-DESIGN.md §3.5 and
// §4.3). The Stripe Connect account IS the destination: a change creates
// a new connected account, opens a payout_destination_change dual-control
// request, and switches only once it's approved (or the single-member
// notice went out), the cooling-off has passed AND Stripe reports the new
// account payouts_enabled (0065 header, Part A).
//
//   GET  /v1/payout-destination                  any member (account_lifecycle.go:44)
//   POST /v1/payout-destination                  owner | payout_approver, step-up (:45-46)
//   POST /v1/payout-destination/onboarding-link  owner | payout_approver, step-up (:47-48)
//
// Stripe sends the publisher back to /payouts/destination/return or
// /refresh (account_lifecycle.go:140, :161); App.tsx forwards those here.
// Approving or canceling the change is the shared Pending approvals
// screen (G-09, org/Approvals.tsx), where either member can cancel.

const HOME = "/publisher";
const APPROVALS = `${HOME}/org/approvals`;

export const IN_FLIGHT_NOTE =
  "A payout that has already started processing keeps the destination it started with: each payout records its destination the moment it enters processing, so a change never redirects money already on its way. Payouts that start after the switch go to the new account.";

const STATE: Record<PayoutDestinationState, { label: string; tone: Tone }> = {
  pending: { label: "Pending change", tone: "warning" },
  active: { label: "Current", tone: "success" },
  superseded: { label: "Replaced", tone: "neutral" },
  canceled: { label: "Canceled", tone: "neutral" },
  retired: { label: "Retired", tone: "neutral" },
};

/** "47 hours 12 minutes": a labeled duration, never a bare "47:12" (UX §3.5). */
export function remainingText(iso: string, now: number = Date.now()): string | null {
  const ms = Date.parse(iso) - now;
  if (Number.isNaN(ms) || ms <= 0) return null;
  const totalMin = Math.ceil(ms / 60_000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const minutes = totalMin % 60;
  const part = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;
  if (days >= 2) return [part(days, "day"), hours ? part(hours, "hour") : null].filter(Boolean).join(" ");
  const h = days * 24 + hours;
  return [h ? part(h, "hour") : null, minutes || !h ? part(minutes, "minute") : null].filter(Boolean).join(" ");
}

function awaitingText(a: PayoutDestinationAwaiting, p: PayoutDestinationPendingChange): string {
  switch (a) {
    case "second_approval":
      return "A second member with authority (an owner or payout approver, other than whoever asked) has to approve it.";
    case "out_of_band_notice":
      return "The notice to the requester's recovery contact hasn't been sent yet (this organization has nobody else who could approve).";
    case "cooling_off": {
      const left = remainingText(p.effective_at);
      return `The waiting period runs until ${formatLocal(p.effective_at)} (your local time)${left ? `, in ${left}` : ""}.`;
    }
    case "stripe_onboarding":
      return "Stripe hasn't enabled payouts on the new account yet. Finish onboarding with Stripe; the switch waits for it, however long that takes.";
  }
}

function openOnboarding(url: string) {
  externalNav.go(url);
}

export function PayoutDestinationCard() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["payout-destination"], queryFn: () => ep.payoutDestination.get() });
  return (
    <Card title="Payout destination" subtitle="Where payouts are sent: your Stripe payout account. Only the last four characters of its id are ever shown.">
      <QueryView query={q} loadingLabel="Loading payout destination">
        {(v) => <DestinationBody v={v} />}
      </QueryView>
    </Card>
  );
}

function DestinationBody({ v }: { v: PayoutDestinationView }) {
  const { me } = useSession();
  const mayChange = canChangePayoutDestination(me.roles);
  return (
    <div className="stack">
      <CurrentDestination current={v.current} />
      {v.pending_change ? <PendingChange p={v.pending_change} mayOnboard={mayChange} /> : null}
      <p className="small" data-testid="in-flight-note">
        {IN_FLIGHT_NOTE}
      </p>
      {mayChange ? (
        <ChangeDestination v={v} />
      ) : (
        <p className="xs muted" data-testid="change-role-note">
          Only an owner or a payout approver can request a change of payout destination.
        </p>
      )}
      <History rows={v.history} />
    </div>
  );
}

function CurrentDestination({ current }: { current: PayoutDestinationVersion | null }) {
  if (!current) {
    return (
      <Banner tone="neutral" title="No payout account yet">
        Set up your first payout account with Stripe under Payout account. Only replacing it later needs a second approver.
      </Banner>
    );
  }
  return (
    <section aria-labelledby="dest-current" className="stack-sm">
      <h3 id="dest-current">Current destination</h3>
      <dl className="definition-list">
        <dt>Stripe account</dt>
        <dd>
          <span className="mono">{current.account_id_masked}</span> (ending {current.last4}) · version {current.version_no}
        </dd>
        <dt>Payouts</dt>
        <dd>
          {current.payouts_enabled ? (
            <StatusPill tone="success">Payouts enabled</StatusPill>
          ) : (
            <StatusPill tone="warning">Payouts not enabled by Stripe</StatusPill>
          )}
        </dd>
        <dt>Verified</dt>
        <dd>{current.verified_at ? formatLocal(current.verified_at) : "Not yet: Stripe hasn't reported payouts enabled"}</dd>
        {current.activated_at ? (
          <>
            <dt>In use since</dt>
            <dd>{formatLocal(current.activated_at)}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

function PendingChange({ p, mayOnboard }: { p: PayoutDestinationPendingChange; mayOnboard: boolean }) {
  const left = remainingText(p.effective_at);
  return (
    <section aria-labelledby="dest-pending" className="stack-sm" data-testid="pending-change">
      <h3 id="dest-pending">Pending change</h3>
      <Banner tone="warning" title={`Changing to the Stripe account ending ${p.new_destination.last4}`}>
        <div className="stack-sm">
          <span>
            Takes effect no earlier than {formatLocal(p.effective_at)} (your local time){left ? ` (in ${left})` : ""}, and only once everything
            below is done. You or another member can cancel it any time before then.
          </span>
          {p.awaiting.length ? (
            <>
              <span>Waiting for:</span>
              <ul className="stack-sm" aria-label="What the change is waiting for">
                {p.awaiting.map((a) => (
                  <li key={a} data-awaiting={a}>
                    {awaitingText(a, p)}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <span>Nothing else is outstanding: it will switch shortly.</span>
          )}
          {p.single_user_fallback ? <span className="small">{SINGLE_USER_FALLBACK_TEXT}</span> : null}
          <span>
            Requested {formatLocal(p.requested_at)}
            {p.approved_at ? `; approved ${formatLocal(p.approved_at)}` : ""}.
          </span>
          <span>
            <Link to={APPROVALS}>Approve or cancel it under Organization → Pending approvals</Link>
          </span>
          {mayOnboard && p.awaiting.includes("stripe_onboarding") ? <OnboardingButton label="Continue Stripe onboarding for the new account" /> : null}
        </div>
      </Banner>
    </section>
  );
}

function OnboardingButton({ label }: { label: string }) {
  const ep = useEndpoints();
  const m = useMutation({
    mutationFn: () => ep.payoutDestination.onboardingLink(),
    onSuccess: (res) => openOnboarding(res.onboarding_url),
  });
  return (
    <div className="stack-sm">
      {m.isError ? (
        <CommandError error={m.error}>
          <RejectionDetail error={m.error} home={HOME} />
        </CommandError>
      ) : null}
      <div>
        <Button variant="secondary" onClick={() => m.mutate()} loading={m.isPending || m.isSuccess}>
          {label}
        </Button>
      </div>
    </div>
  );
}

function ChangeDestination({ v }: { v: PayoutDestinationView }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<RequestPayoutDestinationResponse | null>(null);
  const m = useMutation({
    // One key per "request a change" intent (runCampaignCommand): a retry
    // after a lost response replays it, and the same key scopes the
    // Stripe account creation (account_lifecycle.go:87-92, 111-112).
    mutationFn: () => ep.payoutDestination.request({ idempotencyKey: intent.keyFor("payout-destination-change") }),
    onSuccess: (res) => {
      intent.reset();
      setOpen(false);
      setDone(res);
      void qc.invalidateQueries({ queryKey: ["payout-destination"] });
      void qc.invalidateQueries({ queryKey: ["approvals"] });
      if (res.onboarding_url) openOnboarding(res.onboarding_url);
    },
    onError: () => setOpen(false),
  });
  const { cooling_off_hours: hours, single_member_cooling_off_hours: soloHours } = v.change_requires;
  const blocked = !v.current ? "no_current" : v.pending_change ? "pending" : null;

  return (
    <section aria-labelledby="dest-change" className="stack-sm">
      <h3 id="dest-change">Change destination</h3>
      {done ? (
        <Banner tone="info" title="Change requested" live="polite">
          <div className="stack-sm">
            <span>
              It takes effect no earlier than {formatLocal(done.request.effective_at)} (your local time), after{" "}
              {done.request.single_user_fallback ? "the extended waiting period" : "a second member approves"} and once Stripe enables payouts on the
              new account ending {done.pending_destination.last4}.
            </span>
            {done.onboarding_url ? (
              <span>Taking you to Stripe to set up the new account…</span>
            ) : (
              <OnboardingButton label="Set up the new account with Stripe" />
            )}
          </div>
        </Banner>
      ) : null}
      {m.isError ? (
        <CommandError error={m.error}>
          <RejectionDetail error={m.error} home={HOME} />
        </CommandError>
      ) : null}
      {blocked === "pending" ? (
        <p className="small">A change is already pending. It has to take effect or be canceled before you can request another.</p>
      ) : null}
      {blocked === "no_current" ? <p className="small">There's no payout account to replace yet.</p> : null}
      <div>
        <Button variant="secondary" onClick={() => setOpen(true)} disabled={blocked !== null} loading={m.isPending}>
          Change destination
        </Button>
      </div>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        dismissible={!m.isPending}
        alert
        title="Change where payouts go?"
        description="This decides where your money is sent, so it's protected by a second approver and a waiting period."
        actions={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={m.isPending}>
              Keep the current destination
            </Button>
            <Button onClick={() => m.mutate()} loading={m.isPending}>
              Request the change
            </Button>
          </>
        }
      >
        <ol className="stack-sm small">
          <li>We create a new Stripe payout account and send you to Stripe to add the new bank details. You'll confirm it's you first.</li>
          <li>
            A second member with authority (an owner or payout approver) approves it. If nobody else can, extended verification is used instead: a
            notice to your recovery contact.
          </li>
          <li>
            It takes effect no earlier than {hours} hours after the request ({soloHours / 24} days when nobody else can approve), and only once Stripe
            enables payouts on the new account. Until then, you or another member can cancel it.
          </li>
        </ol>
        <p className="small">{IN_FLIGHT_NOTE}</p>
      </Dialog>
    </section>
  );
}

function History({ rows }: { rows: PayoutDestinationVersion[] }) {
  if (!rows.length) return null;
  return (
    <Table
      caption="Destination history, newest first"
      rows={rows}
      rowKey={(r) => r.destination_id}
      columns={[
        { key: "v", header: "Version", cell: (r) => r.version_no, numeric: true },
        { key: "acct", header: "Stripe account", cell: (r) => <span className="mono">{r.account_id_masked}</span> },
        {
          key: "state",
          header: "State",
          cell: (r) => <StatusPill tone={STATE[r.state].tone}>{STATE[r.state].label}</StatusPill>,
        },
        { key: "payouts", header: "Payouts enabled", cell: (r) => (r.payouts_enabled ? "Yes" : "No") },
        { key: "from", header: "In use from", cell: (r) => (r.activated_at ? formatLocal(r.activated_at) : "—") },
        { key: "to", header: "Until", cell: (r) => (r.ended_at ? formatLocal(r.ended_at) : "—") },
      ]}
    />
  );
}

/** Stripe's return_url / refresh_url for the destination account (account_lifecycle.go:140, :161). */
export function DestinationReturnPage() {
  const { outcome } = useParams();
  const refresh = outcome === "refresh";
  const { me } = useSession();
  return (
    <div className="stack-lg">
      <PageHeader
        title={refresh ? "Onboarding link expired" : "Back from Stripe"}
        back={{ to: `${HOME}/payout-setup`, label: "Payout setup" }}
      />
      {refresh ? (
        <Banner tone="neutral" title="That link can only be used once and has expired">
          <div className="stack-sm">
            <span>Get a fresh link to continue setting up the payout account.</span>
            {canChangePayoutDestination(me.roles) ? <OnboardingButton label="Continue onboarding" /> : null}
          </div>
        </Banner>
      ) : (
        <Banner tone="info" title="Thanks — we're waiting for Stripe" live="polite">
          If you finished, Stripe tells us within a few minutes. A pending change still needs its approval and waiting period before payouts go to
          the new account.
        </Banner>
      )}
      <div>
        <ButtonLink to={`${HOME}/payout-setup`} variant="primary">
          See the payout destination
        </ButtonLink>
      </div>
    </div>
  );
}
